import React, {useEffect, useRef, useState} from 'react'
import {useHistory} from 'react-router'
import * as yup from 'yup'
import {retrieveDealData} from 'Src/actions/DealActions'
import {saveTrade} from 'Src/apis/trade'
import CtaButton from 'Src/components/Buttons/CtaButton'
import InlineButtonSpinner from 'Src/components/Spinners/InlineButtonSpinner'
import {CTA_TYPES} from 'Src/constants'
import {useCustomerDispatchContext, useCustomerStateContext} from 'Src/contexts/CustomerContext'
import {useDealDispatchContext, useDealStateContext} from 'Src/contexts/DealContext'
import {useFrameContext} from 'Src/contexts/FrameContext'
import {useProgressDispatchContext} from 'Src/contexts/ProgressContext'
import {useTradeContext} from 'Src/contexts/TradeContext'
import {ACTION_DETAILS} from 'Src/services/analytics/constants'
import {useAnalytics} from 'Src/services/analytics/useAnalytics'
import {trans} from 'Src/utilities/Helpers'
import useConfig from 'Src/utilities/useConfig'
import useForm from '../../../utilities/useForm'
import Input from '../../Input'

const OwedPayments = () => {
  const {
    a2zApi,
    dealership: {id: dealershipId},
    setMessages,
    useCache,
    vehicleData,
    vin,
    dealershipVehicleMissing,
    isTradeLocked,
  } = useFrameContext()
  const {
    TradeVehicleMileage,
    TradeVehicleCondition,
    TradeVehicleValue,
    TradeVehicleMetadata,
    stillOwePayments,
    setStillOwePayments,
    estimatedPayoffAmount,
    setHasUnsavedTradeData,
    setEstimatedPayoffAmount,
  } = useTradeContext()
  const {customerData, leadId} = useCustomerStateContext()
  const dealState = useDealStateContext()
  const dealDispatch = useDealDispatchContext()
  const customerDispatch = useCustomerDispatchContext()
  const history = useHistory()
  const ProgressDispatch = useProgressDispatchContext()
  const {trackEvent, events} = useAnalytics()
  const config = useConfig()
  const [loading, setLoading] = useState(false)
  const [hasError, setHasError] = useState(false)
  const estimatedPayOffInputRef = useRef()

  useEffect(() => {
    if (stillOwePayments) {
      estimatedPayOffInputRef.current.focus()
    }
  }, [stillOwePayments])

  const updateDesking = async (tradePayload) => {
    customerDispatch({type: 'setTrade', payload: tradePayload})

    return retrieveDealData(
      {
        vin,
        dealershipId,
        useCache,
      },
      dealDispatch,
      a2zApi,
      dealState,
      {customerData, trade: tradePayload},
      false,
      true
    )
  }

  const saveTradeWithLead = async () => {
    const savedResponse = await saveTrade({
      a2zApi: a2zApi,
      leadId: leadId,
      TradeVehicleMetadata: TradeVehicleMetadata,
      TradeVehicleCondition: TradeVehicleCondition,
      TradeVehicleMileage: TradeVehicleMileage,
      TradeVehicleValue: TradeVehicleValue,
      estimatedPayoffAmount: estimatedPayoffAmount,
      setMessages: setMessages,
    })

    if (savedResponse?.errors) {
      setHasError(true)
      setLoading(false)

      return
    }

    if (savedResponse?.data?.data) {
      const newTrade = savedResponse.data.data
      const tradeVehicle = savedResponse.data.included.find(({type}) => type === 'vehicles')

      const tradePayload = {
        id: newTrade.id,
        ...newTrade.attributes,
        vehicle: {id: tradeVehicle?.id, ...tradeVehicle?.attributes},
      }

      await updateDesking(tradePayload)
    }
  }

  const saveTradeWithOutLead = async () => {
    setHasUnsavedTradeData(true)
    const tradePayload = {
      value: TradeVehicleValue,
      payoff: estimatedPayoffAmount,
    }
    await updateDesking(tradePayload)
  }

  const navigateToNextPage = (page) => {
    if (vehicleData.sellingprice < TradeVehicleValue) {
      history.push('/payment-out-of-range')

      return
    }

    trackEvent(events.ENGAGEMENT, events.ENGAGEMENT.actions.CLICK, ACTION_DETAILS.INCLUDE_TRADE_IN)

    // If the url path already contains a query string, use & instead of ?
    history.push(`${page}${page.includes('?') ? '&' : '?'}tradeCompleted=true`)

    ProgressDispatch({type: 'update', payload: {trade: 'complete'}})
  }

  const onSubmit = async () => {
    setHasError(false)

    if (estimatedPayoffAmount) {
      trackEvent(
        events.ENGAGEMENT,
        events.ENGAGEMENT.actions.ADDED,
        ACTION_DETAILS.ESTIMATED_PAYOFF
      )
    }

    setLoading(true)

    const isTradeCTA = config.ctaType === CTA_TYPES.TRADE

    if (leadId) {
      // If the user is logged-in and came from the Trade CTA, we want to navigate to the Desking page (business rule)
      const navigationToPage = isTradeCTA ? '/' : '/trade-complete'

      await saveTradeWithLead()
      setLoading(false)
      navigateToNextPage(navigationToPage)
    } else {
      await saveTradeWithOutLead()
      setLoading(false)

      /**
       *  If the user is not logged-in and came from the Trade CTA and the dealership vehicle is missing,
       * then redirect to the Registration page, otherwise proceed to the Desking page
       */
      let navigationToPage =
        isTradeCTA && !customerData?.id && dealershipVehicleMissing
          ? `/error?message=${encodeURIComponent(trans('vehicle_not_in_inventory'))}`
          : '/'

      if (!isTradeCTA && !isTradeLocked) {
        navigationToPage = '/trade-complete'
      }

      navigateToNextPage(navigationToPage)
    }
  }

  const rules = yup.object().shape(
    stillOwePayments
      ? {
          estimatedPayoffAmount: yup
            .string()
            .label('Estimated payoff amount')
            .required('Estimated payoff amount is required')
            .matches(/^[0-9]*$/, {message: 'Estimated payoff amount must be an integer'})
            .test(
              'not-within-range',
              '${path} must be between 1 and 1,000,000',
              (value) => value >= 1 && value <= 1000000
            ),
        }
      : {}
  )

  const {handleSubmit, getError, clearError} = useForm(
    rules,
    {
      estimatedPayoffAmount: estimatedPayoffAmount || undefined,
    },
    onSubmit
  )

  const ctaText =
    config.ctaType === CTA_TYPES.TRADE
      ? trans('Calculate Payment')
      : trans('trade_int.include_trade')

  return (
    <>
      <form className="md:tw-px-11" onSubmit={handleSubmit} data-testid="owed-payments-form">
        <div className="divider tw-my-12" />
        {TradeVehicleValue ? (
          <>
            <label className="required tw-mb-4 tw-block tw-font-semibold" htmlFor="no-button">
              {trans('trade_int.do_you_owe_payments')}
            </label>
            <div className="tw-mb-10 tw-grid tw-gap-x-6 tw-gap-y-3 sm:tw-grid-cols-2">
              <CtaButton
                preset={stillOwePayments ? 'secondaryActive' : 'secondaryInactive'}
                onClick={() => setStillOwePayments(true)}
                aria-pressed={stillOwePayments}
                data-testid="yes-button"
              >
                {trans('Yes')}
              </CtaButton>
              <CtaButton
                preset={stillOwePayments ? 'secondaryInactive' : 'secondaryActive'}
                onClick={() => {
                  clearError('estimatedPayoffAmount')
                  setStillOwePayments(false)
                  setEstimatedPayoffAmount(0)
                }}
                aria-pressed={!stillOwePayments}
                data-testid="no-button"
                id="no-button"
              >
                {trans('No')}
              </CtaButton>
            </div>
          </>
        ) : null}
        <Input
          ref={estimatedPayOffInputRef}
          label={trans('estimated_payoff')}
          labelStyles="tw-mb-4"
          id="estimatedPayoffAmount"
          name="estimatedPayoffAmount"
          disabled={!stillOwePayments}
          required
          styles="tw-max-w-md"
          type="number"
          value={estimatedPayoffAmount || ''}
          tooltipMessage={trans('estimated_payoff_desc')}
          onChange={(event) => {
            clearError('estimatedPayoffAmount')
            setEstimatedPayoffAmount(event.target.value)
          }}
          error={stillOwePayments ? getError('estimatedPayoffAmount') : ''}
          inputMode="numeric"
        />
        <div className="divider tw-my-12 md:tw-hidden" />
        <div className="tw-mt-12 tw-text-center">
          <CtaButton
            type="submit"
            className="tw-w-full md:tw-max-w-md"
            disabled={!TradeVehicleValue || hasError || loading}
            data-testid="submit-button"
          >
            {loading ? <InlineButtonSpinner /> : <>{ctaText}</>}
          </CtaButton>
        </div>
      </form>
    </>
  )
}

export default OwedPayments
